import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { COLORS } from '../lib/constants';

const S = {
  page: { minHeight: '100vh', background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: 'Cairo, sans-serif' },
  card: { background: '#fff', borderRadius: 24, padding: 32, width: '100%', maxWidth: 420, boxShadow: '0 25px 60px rgba(0,0,0,0.2)' },
  logoText: { fontSize: 32, fontWeight: 900, color: COLORS.primary, letterSpacing: -1, textAlign: 'center' },
  logoSub: { fontSize: 12, color: COLORS.textMuted, textAlign: 'center', marginBottom: 24 },
  tabs: { display: 'flex', background: COLORS.bg, borderRadius: 12, padding: 4, marginBottom: 20, gap: 4 },
  tab: (active) => ({ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'Cairo, sans-serif', background: active ? '#fff' : 'transparent', color: active ? COLORS.primary : COLORS.textMuted, boxShadow: active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none', transition: 'all .2s' }),
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 6 },
  input: { width: '100%', padding: '12px 14px', border: `1.5px solid ${COLORS.border}`, borderRadius: 12, fontSize: 14, fontFamily: 'Cairo, sans-serif', outline: 'none', color: COLORS.text, background: COLORS.bg, boxSizing: 'border-box', marginBottom: 14 },
  btn: { width: '100%', padding: 14, background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', marginTop: 4 },
  error: { background: '#FEF2F2', border: `1px solid #FECACA`, borderRadius: 10, padding: '10px 14px', fontSize: 13, color: COLORS.error, marginBottom: 14, fontFamily: 'Cairo, sans-serif' },
  success: { background: '#F0FDF4', border: `1px solid #BBF7D0`, borderRadius: 10, padding: '10px 14px', fontSize: 13, color: COLORS.success, marginBottom: 14, fontFamily: 'Cairo, sans-serif' },
  langBtn: { position: 'absolute', top: 16, left: 16, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '6px 12px', color: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'Cairo, sans-serif' },
};

export default function AuthPage() {
  const { i18n } = useTranslation();
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [role, setRole] = useState('user');
  const [form, setForm] = useState({ email: '', password: '', full_name: '', phone: '' });
  const [lang, setLang] = useState(i18n.language);
  const isRtl = lang === 'ar';
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const toggleLang = () => {
    const newLang = lang === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    setLang(newLang);
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) { setError(isRtl ? 'أدخل بريدك الإلكتروني' : 'Enter your email'); return; }
    setLoading(true); setError('');
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, { redirectTo: window.location.origin });
    setLoading(false);
    if (error) setError(isRtl ? 'تعذر إرسال رابط الاستعادة، تحقق من البريد وحاول مرة أخرى' : 'Could not send reset link, check the email and try again');
    else setForgotSent(true);
  };

  const handleLogin = async () => {
    if (!form.email || !form.password) { setError(isRtl ? 'أدخل البريد وكلمة المرور' : 'Enter email and password'); return; }
    setLoading(true); setError('');
    const { error } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
    setLoading(false);
    if (error) setError(isRtl ? 'بريد أو كلمة مرور خاطئة' : 'Invalid email or password');
  };

  const handleRegister = async () => {
    if (!form.email || !form.password || !form.full_name) { setError(isRtl ? 'أكمل جميع الحقول' : 'Fill all fields'); return; }
    if (form.password.length < 6) { setError(isRtl ? 'كلمة المرور 6 أحرف على الأقل' : 'Password must be 6+ chars'); return; }
    setLoading(true); setError('');
    const { error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: { data: { full_name: form.full_name, phone: form.phone, role } }
    });
    setLoading(false);
    if (error) setError(error.message);
    else setSuccess(isRtl ? 'تم التسجيل! تحقق من بريدك الإلكتروني' : 'Registered! Check your email');
  };

  return (
    <div style={{ ...S.page, direction: isRtl ? 'rtl' : 'ltr', position: 'relative', flexDirection: 'column' }}>
      
      {/* شريط اللغة العلوي */}
      <div style={{ width: '100%', maxWidth: 420, display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 50, padding: 4, display: 'flex', gap: 4 }}>
          <button onClick={() => { i18n.changeLanguage('ar'); document.documentElement.dir = 'rtl'; setLang('ar'); }} style={{ padding: '8px 20px', borderRadius: 50, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'Cairo, sans-serif', background: lang === 'ar' ? '#fff' : 'transparent', color: lang === 'ar' ? COLORS.primary : '#fff', transition: 'all .2s' }}>
            ع العربية
          </button>
          <button onClick={() => { i18n.changeLanguage('en'); document.documentElement.dir = 'ltr'; setLang('en'); }} style={{ padding: '8px 20px', borderRadius: 50, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'Cairo, sans-serif', background: lang === 'en' ? '#fff' : 'transparent', color: lang === 'en' ? COLORS.primary : '#fff', transition: 'all .2s' }}>
            E English
          </button>
        </div>
      </div>
      <div style={S.card}>
        {/* الشعار */}
        <div style={S.logoText}>{isRtl ? 'جَدوِل' : 'Jadwil'}</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, textAlign: 'center', marginBottom: 4 }}>{isRtl ? 'Jadwil' : 'جَدوِل'}</div>
        <div style={{ fontSize: 13, color: COLORS.textMuted, textAlign: 'center', marginBottom: 24 }}>
          {isRtl ? 'منصة الحجز الذكي' : 'Smart Booking Platform'}
        </div>

        {/* تبويبات تسجيل / دخول */}
        <div style={S.tabs}>
          <button style={S.tab(tab === 'login')} onClick={() => { setTab('login'); setError(''); setSuccess(''); }}>
            {isRtl ? 'تسجيل الدخول' : 'Login'}
          </button>
          <button style={S.tab(tab === 'register')} onClick={() => { setTab('register'); setError(''); setSuccess(''); }}>
            {isRtl ? 'إنشاء حساب' : 'Register'}
          </button>
        </div>

        {error && <div style={S.error}>⚠️ {error}</div>}
        {success && <div style={S.success}>✅ {success}</div>}

        {tab === 'login' ? (
          showForgot ? (
            <>
              {forgotSent ? (
                <div style={S.success}>✅ {isRtl ? 'تم إرسال رابط استعادة كلمة المرور إلى بريدك' : 'A password reset link has been sent to your email'}</div>
              ) : (
                <>
                  <label style={S.label}>{isRtl ? 'البريد الإلكتروني' : 'Email'}</label>
                  <input style={{ ...S.input, direction: isRtl ? 'rtl' : 'ltr' }} type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} />
                  <button style={S.btn} onClick={handleForgotPassword} disabled={loading}>
                    {loading ? (isRtl ? 'جارٍ الإرسال...' : 'Sending...') : (isRtl ? 'إرسال رابط الاستعادة' : 'Send Reset Link')}
                  </button>
                </>
              )}
              <button onClick={() => { setShowForgot(false); setForgotSent(false); setError(''); }} style={{ width: '100%', background: 'none', border: 'none', color: COLORS.primary, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 14, fontFamily: 'Cairo, sans-serif' }}>
                {isRtl ? '← رجوع لتسجيل الدخول' : '← Back to Login'}
              </button>
            </>
          ) : (
            <>
              <label style={S.label}>{isRtl ? 'البريد الإلكتروني' : 'Email'}</label>
              <input style={{ ...S.input, direction: isRtl ? 'rtl' : 'ltr' }} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              <label style={S.label}>{isRtl ? 'كلمة المرور' : 'Password'}</label>
              <input style={{ ...S.input, direction: isRtl ? 'rtl' : 'ltr' }} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
              <button onClick={() => { setShowForgot(true); setError(''); }} style={{ display: 'block', background: 'none', border: 'none', color: COLORS.primary, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', marginBottom: 14, marginTop: -6, fontFamily: 'Cairo, sans-serif', padding: 0 }}>
                {isRtl ? 'نسيت كلمة المرور؟' : 'Forgot password?'}
              </button>
              <button style={S.btn} onClick={handleLogin} disabled={loading}>
                {loading ? (isRtl ? 'جارٍ الدخول...' : 'Loading...') : (isRtl ? 'تسجيل الدخول ←' : 'Login →')}
              </button>
            </>
          )
        ) : (
          <>
            {/* اختيار نوع الحساب */}
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>{isRtl ? 'نوع الحساب' : 'Account Type'}</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setRole('user')} style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: `2px solid ${role === 'user' ? COLORS.primary : COLORS.border}`, background: role === 'user' ? COLORS.primaryLight : '#fff', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: role === 'user' ? COLORS.primary : COLORS.textMuted, transition: 'all .2s' }}>
                  👤 {isRtl ? 'عميل' : 'Customer'}
                </button>
                <button onClick={() => setRole('business')} style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: `2px solid ${role === 'business' ? COLORS.primary : COLORS.border}`, background: role === 'business' ? COLORS.primaryLight : '#fff', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: role === 'business' ? COLORS.primary : COLORS.textMuted, transition: 'all .2s' }}>
                  🏪 {isRtl ? 'شريك' : 'Partner'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 6, textAlign: 'center' }}>
                {role === 'user'
                  ? (isRtl ? 'احجز خدماتك المفضلة' : 'Book your favorite services')
                  : (isRtl ? 'أضف خدماتك واستقبل الحجوزات' : 'Add your services and receive bookings')}
              </div>
            </div>

            <label style={S.label}>{isRtl ? 'الاسم الكامل' : 'Full Name'}</label>
            <input style={{ ...S.input, direction: isRtl ? 'rtl' : 'ltr' }} type="text" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} />
            <label style={S.label}>{isRtl ? 'البريد الإلكتروني' : 'Email'}</label>
            <input style={{ ...S.input, direction: isRtl ? 'rtl' : 'ltr' }} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            <label style={S.label}>{isRtl ? 'رقم الجوال' : 'Phone'}</label>
            <input style={{ ...S.input, direction: isRtl ? 'rtl' : 'ltr' }} type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            <label style={S.label}>{isRtl ? 'كلمة المرور (6 أحرف على الأقل)' : 'Password (6+ chars)'}</label>
            <input style={{ ...S.input, direction: isRtl ? 'rtl' : 'ltr' }} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
            <button style={S.btn} onClick={handleRegister} disabled={loading}>
              {loading ? (isRtl ? 'جارٍ التسجيل...' : 'Loading...') : (isRtl ? 'إنشاء الحساب ←' : 'Create Account →')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
